import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategories,
  fetchCategoryDataById,
  setBaseFields,
  updateCategoryStatus,
} from "../../features/categorySlice";
import Button from "../../components/button/Button";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import { BasicModal } from "../../components/modal/ModalUI";
import CategoryModal from "./CategoryModal";
import { basicModalShowHide, setTitle } from "../../features/modalSlice";
import { can } from "../../utils/permission.util";
import {
  RowActionButton,
  RowActionIcons,
  RowActions,
} from "../../components/data-table/RowActions";

const Category = () => {
  const dispatch = useDispatch();
  const { listData, totalCount, status, error } = useSelector((state) => state.category);
  const [searchText, setSearchText] = useState("");
  // Pagination State
  const [limit, setLimit] = useState(10); // Default limit
  const [offset, setOffset] = useState(0); // Offset starts at 0

  useEffect(() => {
    const handle = setTimeout(() => {
      dispatch(fetchCategories({ offset, limit, searchText }));
    }, searchText ? 400 : 0);
    return () => clearTimeout(handle);
  }, [dispatch, offset, limit, searchText]);

  const addCategory = () => {
    dispatch(basicModalShowHide());
    dispatch(setTitle("Add New Collection"));
  };

  const closeModal = () => {
    dispatch(basicModalShowHide());
    dispatch(setBaseFields());
  };

  function truncateDescription(description) {
    if (!description) return ""; // Handle null, undefined, or empty values
    return description.length > 100
      ? description.substring(0, 100) + "..."
      : description;
  }

  function statusViewString(status) {
    let str = "";
    if (status === 0) {
      str = "Inactive";
    } else if (status === 1) {
      str = "Active";
    } else {
      str = "Delete";
    }

    return str;
  }

  function itemStatusChange(id, status) {
    let req = {
      id: id,
      status: status,
    };

    dispatch(updateCategoryStatus(req));
  }

  function editcategory(id) {
    dispatch(
      fetchCategoryDataById({
        id: id,
      }),
    );
    dispatch(basicModalShowHide());
    dispatch(setTitle("Edit Collection"));
  }

  // Handle Pagination
  const nextPage = () => {
    setOffset((prevOffset) => prevOffset + limit);
  };
  const prevPage = () => {
    setOffset((prevOffset) => Math.max(0, prevOffset - limit));
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setOffset(0); // Reset offset when limit changes
  };

  return (
    <>
      <div id="main" className="main">
        <Breadcrumb page="Collections" />
        <section className="section">
          <div className="row mb-3">
            <div className="col-sm-6 col-md-12 col-lg-12 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2 item_list">
                <h6 className="card-title m-0 p-0">Collections</h6>
                {status === "failed" ? <span className="text-danger">{error || "Unable to load categories."}</span> : null}
                <input
                  className="form-control"
                  placeholder="search"
                  type="text"
                  name="search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              {can("/category", "create") && (
                <Button
                  className="btn btn-sm btn-outline-primary me-2"
                  title="Add Plan"
                  onClick={() => addCategory()}
                >
                  <i className="fa-solid fa-plus" />
                </Button>
              )}
            </div>
          </div>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th scope="col" className="text-center fs-7">
                  Collection Name
                </th>
                <th scope="col" className="text-center fs-7">
                  Description
                </th>
                <th scope="col" className="text-center fs-7">
                  Status
                </th>
                {(can("/category", "create") || can("/category", "delete")) && (
                  <th scope="col" className="text-center fs-7">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {listData && listData.length
                ? listData.map((item, i) => (
                    <tr key={`categoryList-${i}`}>
                      <td className="text-center fs-7" width="10%">
                        {item.name}
                      </td>
                      <td className="text-center fs-7 align-middle">
                        <span title={item.description}>
                          {truncateDescription(item.description)}
                        </span>
                      </td>
                      <td className="text-center fs-7 align-middle">
                        {statusViewString(Number(item.status))}
                      </td>
                      {(can("/category", "create") ||
                        can("/category", "delete")) && (
                        <td className="text-center fs-7 align-middle">
                          <RowActions>
                            {item?.status === 1
                              ? can("/category", "create") && (
                                  <RowActionButton
                                    label="Unpublish"
                                    iconClass={RowActionIcons.unpublish}
                                    variant="outline-success"
                                    onClick={() => itemStatusChange(item.id, 0)}
                                  />
                                )
                              : can("/category", "create") && (
                                  <RowActionButton
                                    label="Publish to store"
                                    iconClass={RowActionIcons.publish}
                                    variant="outline-danger"
                                    onClick={() => itemStatusChange(item.id, 1)}
                                  />
                                )}
                            {can("/category", "create") && (
                              <RowActionButton
                                label="Edit"
                                iconClass={RowActionIcons.edit}
                                variant="outline-success"
                                onClick={() => editcategory(item.id)}
                              />
                            )}
                            {can("/category", "delete") && (
                              <RowActionButton
                                label="Delete"
                                iconClass={RowActionIcons.delete}
                                variant="outline-danger"
                                onClick={() => itemStatusChange(item.id, 2)}
                              />
                            )}
                          </RowActions>
                        </td>
                      )}
                    </tr>
                  ))
                : null}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div>
            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="form-group me-3 d-flex align-items-center">
                {/* <label htmlFor="label">Limit: &nbsp; </label> */}
                <select
                  className="form-select form-select-sm me-2"
                  value={limit}
                  onChange={handleLimitChange}
                >
                  {[10, 20, 50, 100].map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                className="btn btn-outline-primary btn-sm"
                onClick={prevPage}
                disabled={offset === 0}
              >
                Previous
              </Button>

              <Button
                className="btn btn-outline-primary btn-sm"
                onClick={nextPage}
                disabled={
                  offset + limit >= totalCount || (listData || []).length < limit
                }
              >
                Next
              </Button>
            </div>
          </div>
        </section>
        <BasicModal>
          <CategoryModal closeModal={closeModal} />
        </BasicModal>
      </div>
    </>
  );
};

export default Category;
